/**
 * Character Context - Character State Management
 *
 * File: Mythbound.App/src/context/CharacterContext.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-09-21 (PRD001A 001A.4 Wave 3 — provider/navigation half)
 *
 * PURPOSE:
 * React Context for character state management (active character, roster, visibility updates).
 * Manages character CRUD operations via CharacterService.
 *
 * WHO: Mounted per-campaign by `App.tsx`'s `CampaignRouteProviders` wrapper,
 * keyed by `campaignId` (see that file) so switching campaigns fully remounts
 * this provider with fresh state — not just re-fetches into stale state.
 * `CampaignPage.tsx` and `useCharacterWebSocket` read/call this context.
 * WHAT: `status` replaces the previous plain `error: string | null` field.
 * `message` carries a human-readable string for display in the `'error'` /
 * `'forbidden'` / `'not_found'` / `'unavailable'` states; `status` itself is
 * what a caller should branch on, derived from `ApiError.kind` (see
 * `CharacterService.ts`/`ApiError.ts`) rather than string-matching a message.
 * WHERE: src/context/CharacterContext.tsx.
 * WHY (PRD001A 001A.4 Wave 3 / 001A.7a): the 001A.1 inventory found (a) no
 * discrete state existed for "forbidden" vs. "unavailable" vs. "empty
 * roster," so a 503 and a 403 both just set the same `error` string and left
 * `roster` however it last was — indistinguishable from "campaign really has
 * no characters" once `roster` happened to be `[]`; and (b) `updateCharacter`
 * swallowed its error (caught, stored in `error`, never rethrown), so a
 * caller `await`ing it could not tell success from failure. Both are fixed
 * here. Stale-fetch handling: `loadRoster` cancels any previous in-flight
 * roster fetch (via `AbortController`) and additionally guards state updates
 * with a request-generation counter, so a response for a superseded call
 * (old campaign, or a second call before the first resolved) is ignored even
 * if it wasn't aborted in time.
 *
 * STATE:
 * - activeCharacter: Character | null (displayed in sidebar as main hero)
 * - roster: Character[] (all party members)
 * - status: CharacterStatus (see below)
 * - message: string | null (display text for non-'ready' statuses)
 *
 * ACTIONS:
 * - setActiveCharacter(character)
 * - loadRoster(campaignId)
 * - createQuickCharacter(campaignId, name, class?, level, optional narrative)
 * - updateCharacter(characterId, updates) — PUT /{id}, changed fields only, REJECTS on failure
 * - updateVisibility(characterId, visibility)
 * - archiveCharacter(characterId) — POST /{id}/archive (soft delete, recoverable)
 * - restoreCharacter(characterId) — POST /{id}/restore (owner archive list; all roles)
 *
 * USAGE:
 * const { activeCharacter, roster, status, createQuickCharacter } = useCharacter();
 *
 * Pipeline: UI components → CharacterContext → CharacterService → Backend character store
 * Dependencies: React, CharacterService, ApiError, type definitions
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import {
  characterService,
  type Character,
  type CharacterUpdateRequest,
  type ArchiveRestoreResponse,
} from '../services/api/CharacterService';
import { ApiError } from '../services/api/ApiError';

/**
 * Structured roster-load status, replacing the previous `error: string | null`.
 * - `'idle'`: no load attempted yet (initial mount, before `loadRoster` runs).
 * - `'loading'`: a `loadRoster` call is in flight.
 * - `'ready'`: last successful load returned at least one character.
 * - `'empty'`: last successful load returned zero characters — a real empty
 *   roster, never conflated with a failed fetch (see 001A.1 inventory finding).
 * - `'forbidden'`: `ApiError.kind === 'forbidden'` (403), or `'unauthenticated'`
 *   (401) — inside an already-authenticated page shell, a 401 on a
 *   campaign-scoped resource is treated as a permission denial for UI
 *   purposes; the app-wide sign-in gate is `AuthContext`'s job, not this one.
 * - `'not_found'`: `ApiError.kind === 'not_found'` (404).
 * - `'unavailable'`: `ApiError.kind === 'unavailable'` (5xx) or `'network'`
 *   (fetch threw) — never rendered as an empty roster or a false success.
 * - `'error'`: any other `ApiError.kind`, or a non-`ApiError` failure.
 */
export type CharacterStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'empty'
  | 'forbidden'
  | 'not_found'
  | 'unavailable'
  | 'error';

function statusFromError(err: unknown): { status: CharacterStatus; message: string } {
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'forbidden':
      case 'unauthenticated':
        return { status: 'forbidden', message: err.message };
      case 'not_found':
        return { status: 'not_found', message: err.message };
      case 'unavailable':
      case 'network':
        return { status: 'unavailable', message: err.message };
      default:
        return { status: 'error', message: err.message };
    }
  }
  const message = err instanceof Error ? err.message : 'Something went wrong';
  return { status: 'error', message };
}

export interface CharacterContextType {
  // State
  activeCharacter: Character | null;
  roster: Character[];
  /** Owner-only archived rows from the authorized include_archived list. */
  archivedCharacters: Character[];
  archivedLoading: boolean;
  archivedMessage: string | null;
  status: CharacterStatus;
  message: string | null;
  /** Derived convenience flag (`status === 'loading'`) — kept for existing callers. */
  isLoading: boolean;

  // Actions
  setActiveCharacter: (character: Character | null) => void;
  loadRoster: (campaignId: string) => Promise<void>;
  loadArchivedCharacters: (campaignId: string) => Promise<void>;
  createQuickCharacter: (campaignId: string, name: string, charClass: string | undefined, level: number, hitPoints?: number, description?: string, preferences?: Record<string, unknown>) => Promise<Character>;
  /**
   * PUT /{id} partial edit — pass ONLY the fields that changed (see
   * `CharacterUpdateRequest`'s doc comment for omitted-vs-null semantics).
   * Rejects on failure (fixed 001A.4 Wave 3 — see module header).
   */
  updateCharacter: (characterId: string, updates: CharacterUpdateRequest) => Promise<void>;
  updateCharacterHP: (characterId: string, hp: number, maxHp?: number) => Promise<void>;
  updateVisibility: (characterId: string, visibility: Record<string, boolean>) => Promise<void>;
  /** POST /{id}/archive — soft delete, recoverable (replaces the old `deleteCharacter`/DELETE). */
  archiveCharacter: (characterId: string, campaignId?: string) => Promise<ArchiveRestoreResponse>;
  /**
   * POST /{id}/restore. Called by the campaign's minimal owner archive list;
   * server conflicts are rejected and the shared active/archive projections
   * refresh only after a successful persisted restore.
   */
  restoreCharacter: (characterId: string, campaignId?: string) => Promise<ArchiveRestoreResponse>;
  /** Resets `status`/`message` back to `'idle'` — kept name for back-compat; nothing outside this file called it as of the 001A.1 inventory. */
  clearError: () => void;
}

const CharacterContext = createContext<CharacterContextType | undefined>(undefined);

/**
 * CharacterProvider wraps application subtree to provide character state
 *
 * Usage:
 * ```tsx
 * <CharacterProvider key={campaignId} campaignId={campaignId}>
 *   <CampaignPage />
 * </CharacterProvider>
 * ```
 *
 * `campaignId` is read here only to scope the stale-fetch guard below; the
 * actual "start over on campaign switch" behavior comes from the caller
 * keying this component by `campaignId` (see `App.tsx`), which fully
 * unmounts/remounts the provider — this component's own cleanup effect below
 * is what makes that remount cancel any request that was still in flight.
 */
export const CharacterProvider: React.FC<{ children: ReactNode; campaignId?: string }> = ({
  children,
  campaignId
}) => {
  const [activeCharacter, setActiveCharacter] = useState<Character | null>(null);
  const [roster, setRoster] = useState<Character[]>([]);
  const [archivedCharacters, setArchivedCharacters] = useState<Character[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedMessage, setArchivedMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<CharacterStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);

  // Stale-fetch guards for loadRoster specifically (the fetch CampaignPage
  // triggers on mount/campaignId change, and WebSocket handlers re-trigger).
  // requestIdRef: incremented per loadRoster call; a response is applied only
  // if it's still the most recent call when it resolves — protects against a
  // second loadRoster (e.g. a WebSocket character.created event) resolving
  // out of order relative to an earlier one, independent of aborting.
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const archivedRequestIdRef = useRef(0);
  const archivedAbortControllerRef = useRef<AbortController | null>(null);

  const abortInFlightRoster = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  // The active and archived reads have independent generations and abort
  // controllers so one refresh cannot cancel the other. Cleanup on campaign
  // scope change/unmount fences transports that ignore AbortSignal; the
  // protected route unmounts during the current authentication transition.
  useEffect(() => {
    // Clear the previous campaign's private archive projection before the
    // next scope renders; cleanup below also rejects any late old-scope result.
    setArchivedCharacters([]);
    setArchivedMessage(null);
    setArchivedLoading(false);
    return () => {
      abortInFlightRoster();
      archivedAbortControllerRef.current?.abort();
      archivedAbortControllerRef.current = null;
      requestIdRef.current += 1;
      archivedRequestIdRef.current += 1;
    };
  }, [campaignId, abortInFlightRoster]);

  /**
   * Load character roster for campaign.
   *
   * Cancels any previous in-flight roster fetch, and ignores a response that
   * arrives after a newer call has already started (request-generation
   * guard) — see module header for why both mechanisms exist together.
   *
   * @param cId Campaign UUID
   */
  const loadRoster = useCallback(async (cId: string) => {
    abortInFlightRoster();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    setStatus('loading');
    setMessage(null);
    try {
      const characters = await characterService.listCharactersByCampaign(cId, { signal: controller.signal });
      if (requestId !== requestIdRef.current || controller.signal.aborted) return; // superseded or cancelled

      setRoster(characters);
      setStatus(characters.length === 0 ? 'empty' : 'ready');

      // Preserve the explicit selection if it remains in the authorized
      // projection. When it disappears, select only a server-confirmed owner;
      // a first roster entry may be another participant's character.
      // Bug doc: shared/docs/Prompts/PROMPT_FrontendKnownBugs.md § Bug 1
      // Fix: PRD_FrontendFixes_v1.md § Task 1
      setActiveCharacter((prev) => {
        // Rebind selection to the freshly fetched projection. Returning `prev`
        // kept stale fields forever when the character remained in the roster.
        const refreshedSelection = prev && characters.find((c) => c.character_id === prev.character_id);
        return refreshedSelection || characters.find((character) => character.is_owner === true) || null;
      });
    } catch (err) {
      if (requestId !== requestIdRef.current || controller.signal.aborted) return; // superseded or cancelled
      if (err instanceof DOMException && err.name === 'AbortError') return; // intentionally cancelled
      const { status: nextStatus, message: nextMessage } = statusFromError(err);
      setRoster([]);
      setActiveCharacter(null);
      setStatus(nextStatus);
      setMessage(nextMessage);
      console.error('Load roster error:', nextMessage);
    }
  }, [abortInFlightRoster]);

  /**
   * Load active rows and server-authorized archives for this viewer. The
   * server remains membership/ownership authority; exact booleans are checked
   * again so missing projection data fails closed. A separate generation and
   * controller fence stale responses without coupling this read to the active
   * roster request lifecycle.
   */
  const loadArchivedCharacters = useCallback(async (cId: string): Promise<void> => {
    archivedAbortControllerRef.current?.abort();
    const controller = new AbortController();
    archivedAbortControllerRef.current = controller;
    const requestId = ++archivedRequestIdRef.current;
    setArchivedLoading(true);
    setArchivedMessage(null);
    try {
      const rows = await characterService.listCharactersByCampaign(cId, {
        includeArchived: true,
        signal: controller.signal,
      });
      if (requestId !== archivedRequestIdRef.current || controller.signal.aborted) return;
      setArchivedCharacters(rows.filter((row) => row.is_owner === true && row.is_archived === true));
    } catch (err) {
      if (requestId !== archivedRequestIdRef.current || controller.signal.aborted) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setArchivedCharacters([]);
      const { message: nextMessage } = statusFromError(err);
      setArchivedMessage(nextMessage);
    } finally {
      if (requestId === archivedRequestIdRef.current && !controller.signal.aborted) setArchivedLoading(false);
    }
  }, []);
  /**
   * Create a minimal character. Narrative arguments remain optional; undefined
   * fields are omitted on the wire and no sheet facts are fabricated.
   */
  const createQuickCharacter = useCallback(
    async (cId: string, name: string, charClass: string | undefined, level: number, hitPoints?: number, description?: string, preferences?: Record<string, unknown>): Promise<Character> => {
      setMessage(null);
      try {
        const character = await characterService.createQuickCharacter({
          campaign_id: cId,
          character_name: name,
          ...(charClass ? { character_class: charClass } : {}),
          level,
          hit_points: hitPoints,
          ...(description === undefined ? {} : { character_description: description }),
          ...(preferences === undefined ? {} : { character_preferences: preferences }),
        });

        setRoster(prev => [...prev, character]);
        setActiveCharacter(character);
        setStatus('ready');
        return character;
      } catch (err) {
        const { status: nextStatus, message: nextMessage } = statusFromError(err);
        setStatus(nextStatus);
        setMessage(nextMessage);
        console.error('Create character error:', nextMessage);
        throw err;
      }
    },
    []
  );

  /**
   * Update character sheet attributes — optimistic local merge.
   *
   * FIXED 2026-09-21 (PRD001A 001A.4 Wave 3): this previously caught its own
   * error, stored it in `error`, and returned normally — a caller awaiting
   * this promise could not tell a failed update from a successful one. It
   * now rethrows, matching every other mutating action in this context.
   *
   * @param characterId Character UUID
   * @param updates Partial character fields to update
   * @throws rethrows the underlying `ApiError`/`Error` on failure
   */
  const updateCharacter = useCallback(
    async (characterId: string, updates: CharacterUpdateRequest): Promise<void> => {
      setMessage(null);
      try {
        const updatedCharacter = await characterService.updateCharacter(characterId, updates);
        setRoster((prev) =>
          prev.map((c) => (c.character_id === characterId ? updatedCharacter : c))
        );
      } catch (err) {
        const { status: nextStatus, message: nextMessage } = statusFromError(err);
        setStatus(nextStatus);
        setMessage(nextMessage);
        console.error('[CharacterContext] updateCharacter failed:', nextMessage, { characterId, updates });
        throw err;
      }
    },
    []
  );

  /**
   * Update character HP (current and/or max)
   */
  const updateCharacterHP = useCallback(
    async (characterId: string, hp: number, maxHp?: number): Promise<void> => {
      setMessage(null);
      try {
        await characterService.updateCharacterHP(characterId, { hp, max_hp: maxHp });

        setRoster(prev =>
          prev.map(c => c.character_id === characterId ? { ...c, hp, max_hp: maxHp || c.max_hp } : c)
        );

        setActiveCharacter(prev =>
          prev && prev.character_id === characterId ? { ...prev, hp, max_hp: maxHp || prev.max_hp } : prev
        );
      } catch (err) {
        const { status: nextStatus, message: nextMessage } = statusFromError(err);
        setStatus(nextStatus);
        setMessage(nextMessage);
        console.error('Update HP error:', nextMessage);
        throw err;
      }
    },
    []
  );

  /**
   * Update character visibility settings (per-field checkboxes)
   */
  const updateVisibility = useCallback(async (characterId: string, visibility: Record<string, boolean>) => {
    setMessage(null);
    try {
      await characterService.updateCharacterVisibility(characterId, visibility);
      const updated = await characterService.getCharacter(characterId);
      setRoster(prev => prev.map(c => c.character_id === characterId ? updated : c));
      setActiveCharacter(prev => (prev && prev.character_id === characterId ? updated : prev));
    } catch (err) {
      const { status: nextStatus, message: nextMessage } = statusFromError(err);
      setStatus(nextStatus);
      setMessage(nextMessage);
      console.error('Update visibility error:', nextMessage);
      throw err;
    }
  }, []);

  /**
   * Archive character (soft delete, recoverable) — POST /{id}/archive.
   * REPLACES the old `deleteCharacter`/`DELETE {id}` (001A.4: that verb is
   * not registered on the backend at all — see `CharacterService.archiveCharacter`).
   * Removes the character from the active roster locally; the row itself is
   * never destroyed server-side, and session/membership references to it
   * are unaffected.
   */
  const archiveCharacter = useCallback(async (characterId: string, requestedCampaignId?: string): Promise<ArchiveRestoreResponse> => {
    setMessage(null);
    const scopedCampaignId = requestedCampaignId ?? roster.find((row) => row.character_id === characterId)?.campaign_id;
    try {
      const result = await characterService.archiveCharacter(characterId);
      // Update immediately after persistence succeeds, then reconcile both
      // shared projections from the authorized server response. A failed write
      // never removes the visible character; refresh failures remain visible
      // through their own roster/list state and can be retried.
      setRoster((prev) => {
        const next = prev.filter((c) => c.character_id !== characterId);
        setActiveCharacter((prevActive) => prevActive?.character_id === characterId ? next.find((character) => character.is_owner === true) || null : prevActive);
        return next;
      });
      if (scopedCampaignId) {
        await loadRoster(scopedCampaignId);
        await loadArchivedCharacters(scopedCampaignId);
      }
      return result;
    } catch (err) {
      const { status: nextStatus, message: nextMessage } = statusFromError(err);
      setStatus(nextStatus);
      setMessage(nextMessage);
      console.error('Archive character error:', nextMessage);
      throw err;
    }
  }, [loadRoster, loadArchivedCharacters, roster]);

  /**
   * Restore an owner-visible archived character — POST /{id}/restore. No
   * tier or Game Maker gate exists in this UI; the backend verifies ownership
   * and rejects an active-character conflict without altering the other row.
   */
  const restoreCharacter = useCallback(async (characterId: string, requestedCampaignId?: string): Promise<ArchiveRestoreResponse> => {
    setMessage(null);
    const scopedCampaignId = requestedCampaignId ?? archivedCharacters.find((row) => row.character_id === characterId)?.campaign_id;
    try {
      const result = await characterService.restoreCharacter(characterId);
      // Restore is committed by the server before either view is refreshed.
      // A 409 active_character_exists leaves both projections untouched and
      // reaches the caller so the conflict can be explained without guessing.
      if (scopedCampaignId) {
        await loadRoster(scopedCampaignId);
        await loadArchivedCharacters(scopedCampaignId);
      }
      return result;
    } catch (err) {
      const { status: nextStatus, message: nextMessage } = statusFromError(err);
      setStatus(nextStatus);
      setMessage(nextMessage);
      console.error('Restore character error:', nextMessage);
      throw err;
    }
  }, [archivedCharacters, loadRoster, loadArchivedCharacters]);

  const clearError = useCallback(() => {
    setStatus('idle');
    setMessage(null);
  }, []);

  const value: CharacterContextType = {
    activeCharacter,
    roster,
    archivedCharacters,
    archivedLoading,
    archivedMessage,
    status,
    message,
    isLoading: status === 'loading',
    setActiveCharacter,
    loadRoster,
    loadArchivedCharacters,
    createQuickCharacter,
    updateCharacter,
    updateCharacterHP,
    updateVisibility,
    archiveCharacter,
    restoreCharacter,
    clearError
  };

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};

/**
 * Hook to use character context
 *
 * @returns CharacterContextType
 * @throws Error if used outside CharacterProvider
 */
export const useCharacter = (): CharacterContextType => {
  const context = useContext(CharacterContext);
  if (!context) {
    throw new Error('useCharacter must be used within CharacterProvider');
  }
  return context;
};
